import React, { useMemo, useRef, useEffect, useState } from 'react';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';

const superCleanString = (str) => {
    if (!str) return '';
    return str
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .trim();
};

const parseDateToObj = (dateStr) => {
    if (!dateStr) return null;
    if (typeof dateStr === 'string' && dateStr.includes('-')) {
        const parts = dateStr.split('-');
        if (parts.length === 3) {
            return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
        }
    }
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
};

const SearchableFocusDropdown = ({ value, options, onChange, placeholder, disabled, panelUniqueClass, className, filterBy = "label,searchKey", itemTemplate, ...rest }) => {
    const dropdownRef = useRef(null);
    const focusTrapRef = useRef(null);

    const handleShow = () => {
        setTimeout(() => {
            try {
                const panel = document.querySelector(`.${panelUniqueClass}`);
                if (panel) {
                    const filterInput = panel.querySelector('.p-dropdown-filter');
                    if (filterInput && typeof filterInput.focus === 'function') {
                        filterInput.focus();

                        focusTrapRef.current = (e) => {
                            if (e.target !== filterInput) {
                                filterInput.focus();
                            }
                        };

                        panel.addEventListener('focusin', focusTrapRef.current);
                    }
                }
            } catch (err) {
                console.warn("Focus error:", err);
            }
        }, 100);
    };

    const handleHide = () => {
        const panel = document.querySelector(`.${panelUniqueClass}`);
        if (panel && focusTrapRef.current) {
            panel.removeEventListener('focusin', focusTrapRef.current);
        }
    };

    return (
        <Dropdown
            ref={dropdownRef}
            value={value}
            options={options}
            onChange={onChange}
            filter
            filterBy={filterBy}
            placeholder={placeholder}
            className={className || "custom-dropdown"}
            panelClassName={panelUniqueClass}
            style={{ width: '100%' }}
            appendTo={document.body}
            onShow={handleShow}
            onHide={handleHide}
            disabled={disabled}
            itemTemplate={itemTemplate}
            emptyFilterMessage="Không tìm thấy kết quả"
            {...rest}
        />
    );
};

const PublicationOtherForm = ({
    isOpen,
    onClose,
    formData,
    setFormData,
    isEditing,
    contentCategories = [],
    users = [],
    members = [],
    setMembers,
    toast,
    fetchData
}) => {
    const [pendingFiles, setPendingFiles] = useState([]);
    const [deletedFileIds, setDeletedFileIds] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setPendingFiles([]);
            setDeletedFileIds([]);
            setIsSubmitting(false);

            if (!isEditing && members.length === 0) {
                const currentUser = JSON.parse(localStorage.getItem('user')) || {};
                setMembers([{
                    userId: currentUser.Id || '',
                    fullName: `${currentUser.LastName || ''} ${currentUser.FirstName || ''}`.trim(),
                    isFirstAuthor: true
                }]);
            }
        }
    }, [isOpen, isEditing]);

    const existingFiles = useMemo(() => {
        if (!formData.AttachmentUrl) return [];
        try {
            const parsed = JSON.parse(formData.AttachmentUrl);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return [];
        }
    }, [formData.AttachmentUrl]);

    const userOptions = useMemo(() => {
        return users.map(u => {
            const fullName = `${u.LastName || ''} ${u.FirstName || ''}`.trim();
            const email = u.Email || u.email || '';
            const dept = u.DepartmentName || '';
            return {
                label: email ? `${fullName} - ${email}` : fullName,
                value: u.Id,
                searchKey: superCleanString(`${fullName} ${email} ${dept}`),
                fullName: fullName,
                email: email,
                dept: dept
            };
        });
    }, [users]);

    const userItemTemplate = (option) => (
        <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
            <span style={{ fontWeight: 'bold', color: '#333' }}>{option.fullName}</span>
            <span style={{ fontSize: '12px', color: '#666' }}>{option.email} {option.dept ? ` - ${option.dept}` : ''}</span>
        </div>
    );

    const groupedCategories = useMemo(() => {
        const groups = {
            "Hướng dẫn SV NCKH / Hướng dẫn đề tài SV NCKH": [],
            "Chuyển giao công nghệ": [],
            "Sở hữu trí tuệ": [],
            "Viết đề xuất đề tài": [],
            "Viết thuyết minh đề tài": [],
            "Tham vấn chính sách": [],
            "Phản biện": [],
            "Báo cáo khoa học": [],
            "Khác": []
        };

        contentCategories.forEach(cat => {
            const rawName = cat.Name || cat.ContentName || "";
            const displayLabel = cat.Score !== undefined ? `${rawName} (${cat.Score} điểm)` : rawName;

            const item = {
                label: displayLabel,
                value: displayLabel,
                searchKey: superCleanString(displayLabel)
            };

            const nameLower = rawName.toLowerCase();

            if (nameLower.includes("hướng dẫn sv") || nameLower.includes("hướng dẫn sinh viên") || nameLower.includes("hướng dẫn đề tài")) {
                groups["Hướng dẫn SV NCKH / Hướng dẫn đề tài SV NCKH"].push(item);
            } else if (nameLower.includes("chuyển giao công nghệ")) {
                groups["Chuyển giao công nghệ"].push(item);
            } else if (nameLower.includes("sở hữu trí tuệ") || nameLower.includes("sáng chế") || nameLower.includes("giải pháp")) {
                groups["Sở hữu trí tuệ"].push(item);
            } else if (nameLower.includes("viết đề xuất") || nameLower.includes("đề xuất đề tài")) {
                groups["Viết đề xuất đề tài"].push(item);
            } else if (nameLower.includes("thuyết minh")) {
                groups["Viết thuyết minh đề tài"].push(item);
            } else if (nameLower.includes("tham vấn")) {
                groups["Tham vấn chính sách"].push(item);
            } else if (nameLower.includes("phản biện")) {
                groups["Phản biện"].push(item);
            } else if (nameLower.includes("báo cáo")) {
                groups["Báo cáo khoa học"].push(item);
            } else {
                groups["Khác"].push(item);
            }
        });

        return Object.keys(groups)
            .filter(key => groups[key].length > 0)
            .map(key => ({
                label: key,
                items: groups[key]
            }));
    }, [contentCategories]);

    const contentLower = (formData.ContentName || "").toLowerCase();
    const isIP = contentLower.includes('sáng chế') || contentLower.includes('sở hữu trí tuệ') || contentLower.includes('giải pháp');
    const isStudentGuide = contentLower.includes("hướng dẫn sv") || contentLower.includes("hướng dẫn sinh viên") || contentLower.includes("hướng dẫn đề tài");
    const is2GV = contentLower.includes("(02 gv)");
    const is2GVStudentGuide = isStudentGuide && is2GV;

    if (!isOpen) return null;

    const currentUserStr = localStorage.getItem('user');
    const currentUserInfo = currentUserStr ? JSON.parse(currentUserStr) : null;
    const isAdminModal = currentUserInfo?.RoleId === 1 || currentUserInfo?.RoleId === 0;
    const isCreator = !isEditing || (formData.UserId?.toString() === currentUserInfo?.Id?.toString());
    const isReadOnlyForm = isEditing && !isAdminModal && !isCreator;

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        setFormData({ ...formData, [name]: type === 'number' ? (value === '' ? '' : Number(value)) : value });
    };

    const handleDateChange = (name, dateObj) => {
        let dateString = '';
        if (dateObj) {
            const yyyy = dateObj.getFullYear();
            const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
            const dd = String(dateObj.getDate()).padStart(2, '0');
            dateString = `${yyyy}-${mm}-${dd}`;
        }
        handleChange({ target: { name, value: dateString, type: 'text' } });
    };

    const handleContentSelection = (selectedValue) => {
        const valLower = (selectedValue || "").toLowerCase();
        const checkIP = valLower.includes('sáng chế') || valLower.includes('sở hữu trí tuệ') || valLower.includes('giải pháp');
        const checkStudentGuide = valLower.includes("hướng dẫn sv") || valLower.includes("hướng dẫn sinh viên") || valLower.includes("hướng dẫn đề tài");
        const check2GV = valLower.includes("(02 gv)");
        const check2GVStudentGuide = checkStudentGuide && check2GV;

        let updatedMembers = [...members];

        if (check2GVStudentGuide) {
            while (updatedMembers.length < 2) {
                updatedMembers.push({ userId: '', fullName: '', isFirstAuthor: false });
            }
            updatedMembers = updatedMembers.slice(0, 2);
            setFormData({ ...formData, ContentName: selectedValue, MemberCount: 2, MembersJSON: JSON.stringify(updatedMembers) });
        } else if (checkIP) {
            if (updatedMembers.length === 0) {
                const currentUser = JSON.parse(localStorage.getItem('user')) || {};
                updatedMembers.push({ userId: currentUser.Id || '', fullName: `${currentUser.LastName || ''} ${currentUser.FirstName || ''}`.trim(), isFirstAuthor: true });
            }
            setFormData({ ...formData, ContentName: selectedValue, MemberCount: updatedMembers.length, MembersJSON: JSON.stringify(updatedMembers) });
        } else {
            setFormData({ ...formData, ContentName: selectedValue });
        }

        setMembers(updatedMembers);
        handleContentBlur(selectedValue);
    };

    const handleContentBlur = async (contentText) => {
        if (!contentText || contentText.trim().length < 5) return;

        try {
            const userStr = localStorage.getItem('user');
            const currentUser = userStr ? JSON.parse(userStr) : null;
            const userId = currentUser ? currentUser.Id : '';

            const userRoleName = (currentUser?.RoleName || '').toLowerCase();
            const isAdmin = userRoleName === 'admin' || currentUser?.RoleId === 1 ||
                (userRoleName.includes('trưởng khoa') && currentUser?.IsDelegatedAdmin === true);

            const baseUrl = process.env.REACT_APP_API_BASE_URL;
            const response = await fetch(`${baseUrl}/publicationotherresearch?userId=${userId}&isAdmin=${isAdmin}`, {
                headers: {
                    'X-User-Role-Id': currentUser?.RoleId?.toString() || '',
                    'X-User-Dept-Id': currentUser?.DepartmentId?.toString() || ''
                }
            });

            if (response.ok) {
                const existingData = await response.json();

                if (!Array.isArray(existingData)) return;

                const normalizeString = (str) => {
                    if (!str) return '';
                    return str.toLowerCase().trim()
                        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
                        .replace(/đ/g, 'd').replace(/Đ/g, 'D')
                        .replace(/[^a-z0-9]/g, "");
                };

                const normInput = normalizeString(contentText);
                const currentId = isEditing ? formData.Id : 0;
                const inputYear = formData.ApplyDate ? formData.ApplyDate.substring(0, 4) : '';

                for (let item of existingData) {
                    if (item.Id === currentId) continue;
                    const normExisting = normalizeString(item.ContentName);

                    if (normInput === normExisting) {
                        const existingYear = item.ApplyDate ? item.ApplyDate.substring(0, 4) : '';
                        if (existingYear === inputYear) {
                            toast?.current?.show({
                                severity: 'info',
                                summary: 'Lưu ý trùng lặp',
                                detail: `Hệ thống phát hiện nội dung tương tự đã được kê khai: "${item.ContentName}". Nếu đây là một đợt kê khai mới, bạn có thể bỏ qua thông báo này.`,
                                life: 8000
                            });
                            break;
                        }
                    }
                }
            }
        } catch (err) {
            console.error("Lỗi check trùng lặp:", err);
        }
    };

    const handleMultiFileChange = (e) => {
        if (isReadOnlyForm) return;

        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
        const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png'];
        const MAX_FILE_SIZE = 5 * 1024 * 1024;

        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const validFiles = files.filter(file => {
            const isTypeValid = allowedTypes.includes(file.type);
            const fileName = file.name.toLowerCase();
            const isExtensionValid = allowedExtensions.some(ext => fileName.endsWith(ext));

            if (!isTypeValid && !isExtensionValid) {
                toast?.current?.show({
                    severity: 'warn',
                    summary: 'Định dạng không hợp lệ',
                    detail: `Tệp "${file.name}" không được hỗ trợ. Chỉ cho phép PDF, JPG/JPEG, PNG`,
                    life: 5000
                });
                return false;
            }

            if (file.size > MAX_FILE_SIZE) {
                toast?.current?.show({
                    severity: 'warn',
                    summary: 'Dung lượng quá lớn',
                    detail: `Tệp "${file.name}" vượt quá giới hạn 5MB. Vui lòng chọn tệp nhỏ hơn.`,
                    life: 5000
                });
                return false;
            }

            return true;
        });

        if (validFiles.length === 0) {
            e.target.value = null;
            return;
        }

        const newFilesArray = validFiles.map(file => ({
            fileObj: file,
            name: file.name,
            description: ''
        }));

        setPendingFiles(prev => [...prev, ...newFilesArray]);
        e.target.value = null;
    };

    const handleFileDescChange = (index, desc) => {
        if (isReadOnlyForm) return;
        const updated = [...existingFiles];
        updated[index].description = desc;
        setFormData({ ...formData, AttachmentUrl: JSON.stringify(updated) });
    };

    const removeExistingFile = (fileId) => {
        if (isReadOnlyForm) return;
        setDeletedFileIds(prev => [...prev, fileId]);
        const updated = existingFiles.filter(f => f.fileId !== fileId && f.url !== fileId && f.link !== fileId && f.data !== fileId);
        setFormData({ ...formData, AttachmentUrl: JSON.stringify(updated) });
    };

    const removePendingFile = (index) => {
        if (isReadOnlyForm) return;
        setPendingFiles(pendingFiles.filter((_, i) => i !== index));
    };

    const downloadIndividualFile = async (fileObj) => {
        let fileUrl = '';
        if (fileObj.fileId) {
            fileUrl = `${process.env.REACT_APP_API_BASE_URL}/downloadpublicationotherevidence?fileId=${fileObj.fileId}`;
        } else if (fileObj.url || fileObj.data || fileObj.link || fileObj.Link || fileObj.URL) {
            fileUrl = fileObj.url || fileObj.data || fileObj.link || fileObj.Link || fileObj.URL;
            if (typeof fileUrl === 'string') {
                fileUrl = fileUrl.trim();
                if (fileUrl.startsWith('/due-bucket') || fileUrl.startsWith('/uploads')) {
                    fileUrl = `https://data.tapchikhkt.vn${fileUrl}`;
                }
            }
        }

        if (!fileUrl) {
            toast?.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không tìm thấy đường dẫn tệp!', life: 3000 });
            return;
        }

        const base64ToBlob = (dataUrl) => {
            const arr = dataUrl.split(',');
            const mime = arr[0].match(/:(.*?);/)?.[1] || 'application/octet-stream';
            const bstr = atob(arr[1]);
            let n = bstr.length;
            const u8arr = new Uint8Array(n);
            while (n--) {
                u8arr[n] = bstr.charCodeAt(n);
            }
            return new Blob([u8arr], { type: mime });
        };

        try {
            if (typeof fileUrl === 'string' && fileUrl.startsWith('data:')) {
                const blob = base64ToBlob(fileUrl);
                const objectUrl = URL.createObjectURL(blob);

                const newWindow = window.open(objectUrl, '_blank');
                if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                    const link = document.createElement('a');
                    link.href = objectUrl;
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }

                setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
                return;
            }

            const newWindow = window.open(fileUrl, '_blank');
            if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                const link = document.createElement('a');
                link.href = fileUrl;
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (error) {
            console.error("Lỗi khi xử lý mở file:", error);
            toast?.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không thể hiển thị tệp này do lỗi định dạng!', life: 3000 });
        }
    };

    const handleAddMember = () => {
        if (isReadOnlyForm) return;
        const newMembers = [...members, { userId: '', fullName: '', isFirstAuthor: false }];
        setMembers(newMembers);
        setFormData(prev => ({
            ...prev,
            MemberCount: newMembers.length,
            MembersJSON: JSON.stringify(newMembers)
        }));
    };

    const handleRemoveMember = (index) => {
        if (isReadOnlyForm) return;
        const newMembers = members.filter((_, i) => i !== index);
        setMembers(newMembers);
        setFormData(prev => ({
            ...prev,
            MemberCount: Math.max(1, newMembers.length),
            MembersJSON: JSON.stringify(newMembers)
        }));
    };

    const handleMemberChange = (index, field, value) => {
        if (isReadOnlyForm) return;
        const updated = [...members];
        updated[index][field] = value;

        if (field === 'userId' && value) {
            const selectedUser = users.find(u => u.Id === value);
            if (selectedUser) {
                updated[index].fullName = `${selectedUser.LastName || ''} ${selectedUser.FirstName || ''}`.trim();
            }
        }

        setMembers(updated);

        let primaryCount = formData.PrimaryAuthors;
        if (field === 'isFirstAuthor') {
            primaryCount = updated.filter(m => m.isFirstAuthor).length;
            primaryCount = primaryCount > 0 ? primaryCount : 1;
        }

        setFormData(prev => ({
            ...prev,
            PrimaryAuthors: primaryCount,
            MemberCount: (isIP || is2GVStudentGuide) ? updated.length : prev.MemberCount,
            MembersJSON: JSON.stringify(updated)
        }));
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (isReadOnlyForm) return;

        if (existingFiles.length === 0 && pendingFiles.length === 0) {
            toast?.current?.show({
                severity: 'warn',
                summary: 'Thiếu thông tin',
                detail: 'Vui lòng đính kèm ít nhất một tệp tin minh chứng!',
                life: 4000
            });
            return;
        }

        const currentUser = JSON.parse(localStorage.getItem('user')) || {};
        if (!currentUser || !currentUser.Id) {
            toast?.current?.show({ severity: 'error', summary: 'Lỗi xác thực', detail: 'Phiên đăng nhập hết hạn!', life: 3000 });
            return;
        }

        setIsSubmitting(true);

        let cleanEvidence = [];
        try {
            const currentFiles = JSON.parse(formData.AttachmentUrl || '[]');
            cleanEvidence = currentFiles.filter(f => !f.fileId);
        } catch (err) { }

        const payload = {
            ...formData,
            UserId: parseInt(currentUser.Id),
            MemberCount: (isIP || is2GVStudentGuide) ? members.length : parseInt(formData.MemberCount || 1),
            MembersJSON: JSON.stringify(members),
            AttachmentUrl: JSON.stringify(cleanEvidence),
            Id: isEditing ? formData.Id : 0
        };

        const method = isEditing ? 'PUT' : 'POST';

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/publicationotherresearch`, {
                method,
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
                    'Content-Type': 'application/json; charset=UTF-8'
                },
                body: JSON.stringify(payload)
            });

            if (response.status === 401 || response.status === 403) {
                toast?.current?.show({ severity: 'error', summary: 'Lỗi xác thực', detail: 'Phiên đăng nhập hết hạn!', life: 3000 });
                return;
            }

            let result;
            try {
                result = await response.json();
            } catch (err) {
                result = { status: 'error', message: 'Lỗi máy chủ không xác định!' };
            }

            if (response.ok && result.status !== 'error') {
                const finalPubId = result.publicationOtherId || formData.Id;

                if (deletedFileIds.length > 0) {
                    await Promise.all(deletedFileIds.map(async (fId) => {
                        const parsedId = parseInt(fId, 10);
                        if (!isNaN(parsedId)) {
                            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/deletepublicationotherevidence?fileId=${parsedId}`, {
                                method: 'DELETE',
                                headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` }
                            });

                            if (!res.ok) {
                                console.error(`Không thể xóa file cũ có ID: ${parsedId}`);
                            }
                        }
                    }));
                }

                if (pendingFiles.length > 0) {
                    await Promise.all(pendingFiles.map(async (pf) => {
                        const fd = new FormData();
                        fd.append('publicationOtherId', finalPubId);
                        fd.append('description', pf.description);
                        fd.append('file', pf.fileObj);

                        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/uploadpublicationotherevidence`, {
                            method: 'POST',
                            headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` },
                            body: fd
                        });

                        if (!res.ok) {
                            const err = await res.json().catch(() => ({}));
                            throw new Error(err.message || `Upload file ${pf.name} thất bại!`);
                        }
                    }));
                }

                toast?.current?.show({ severity: 'success', summary: 'Thành công', detail: isEditing ? 'Cập nhật thành công!' : 'Thêm mới thành công!', life: 3000 });
                if (typeof fetchData === 'function') fetchData(true);
                onClose();
            } else {
                toast?.current?.show({ severity: 'error', summary: 'Không thể lưu', detail: result.message || 'Lưu thất bại!', life: 6000 });
            }
        } catch (error) {
            toast?.current?.show({ severity: 'error', summary: 'Lỗi', detail: error.message || 'Lỗi kết nối máy chủ!', life: 3000 });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="modal-overlay" style={{ zIndex: 1000 }}>
            <div className="modal-box form-modal-box" style={{ maxHeight: '90vh', overflowY: 'auto' }}>

                <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <h3 style={{ margin: 0, paddingRight: '15px', lineHeight: '1.4' }}>
                        {isReadOnlyForm ? "Xem chi tiết Nội dung NCKH khác" : (isEditing ? "Cập nhật Nội dung NCKH khác" : "Thêm mới Nội dung nghiên cứu khoa học khác")}
                    </h3>
                    <button className="close-btn" onClick={onClose} style={{ fontSize: '26px', lineHeight: '1', display: 'flex', flexShrink: 0, marginTop: '-2px' }}>
                        &times;
                    </button>
                </div>

                <div className="modal-body" style={{ padding: '25px' }}>
                    <form id="publicationOtherForm" onSubmit={handleFormSubmit}>

                        <div className="form-group" style={{ marginBottom: '25px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Nội dung *</label>
                            <SearchableFocusDropdown
                                value={formData.ContentName}
                                options={groupedCategories}
                                onChange={(e) => handleContentSelection(e.value)}
                                optionLabel="label"
                                optionGroupLabel="label"
                                optionGroupChildren="items"
                                optionGroupTemplate={(option) => (
                                    <div style={{
                                        padding: '12px 15px',
                                        fontWeight: 'bold',
                                        color: '#0056b3',
                                        backgroundColor: '#f1f8ff',
                                        borderBottom: '1px solid #dce9f5',
                                        fontSize: '14px'
                                    }}>
                                        {option.label}
                                    </div>
                                )}
                                itemTemplate={(option) => (
                                    <div style={{
                                        padding: '10px 15px',
                                        whiteSpace: 'normal',
                                        lineHeight: '1.5',
                                        fontSize: '14px',
                                        borderBottom: '1px dashed #f0f0f0'
                                    }}>
                                        {option.label}
                                    </div>
                                )}
                                filterPlaceholder="Tìm kiếm"
                                placeholder="Chọn nội dung nghiên cứu"
                                className="w-full form-input custom-prime-dropdown"
                                panelUniqueClass="custom-dropdown-panel-contentname"
                                style={{
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    height: '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    paddingLeft: '12px'
                                }}
                                panelStyle={{
                                    maxWidth: '750px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                                }}
                                disabled={isReadOnlyForm}
                            />
                        </div>

                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '10px' }}>
                            <div className="form-group" style={{ flex: '1 1 150px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Ngày áp dụng *</label>
                                <Calendar
                                    id="ApplyDate"
                                    name="ApplyDate"
                                    value={parseDateToObj(formData.ApplyDate)}
                                    dateFormat="dd/mm/yy"
                                    showIcon
                                    onChange={(e) => handleDateChange('ApplyDate', e.value)}
                                    style={{ width: '100%' }}
                                    appendTo={document.body}
                                    panelStyle={{ marginTop: '5px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', borderRadius: '8px', zIndex: 99999 }}
                                    inputStyle={{ padding: '10px 12px', border: '1px solid #cbd5e1', borderRight: 'none', borderRadius: '6px 0 0 6px', fontSize: '14px', outline: 'none' }}
                                    disabled={isReadOnlyForm}
                                    required
                                />
                            </div>
                            <div className="form-group" style={{ flex: '1 1 150px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Số lượng đề tài *</label>
                                <input type="number" min="1" name="Quantity" className="form-input" value={formData.Quantity} onChange={handleChange} required disabled={isReadOnlyForm} />
                            </div>
                            {!is2GVStudentGuide && (
                                <div className="form-group" style={{ flex: '1 1 150px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Số thành viên tham gia *</label>
                                    <input
                                        type="number"
                                        min="1"
                                        name="MemberCount"
                                        className="form-input"
                                        value={isIP ? Math.max(1, members.length) : (formData.MemberCount || 1)}
                                        onChange={handleChange}
                                        required
                                        disabled={isReadOnlyForm || isIP}
                                    />
                                </div>
                            )}
                        </div>

                        {isIP && (
                            <div style={{ marginTop: '20px', marginBottom: '20px', padding: '15px', border: '1px solid #3498db', borderRadius: '5px', backgroundColor: '#f0f8ff' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                    <label style={{ fontWeight: 'bold', color: '#0056b3' }}>Kê khai Thành viên Sở hữu trí tuệ *</label>
                                    {!isReadOnlyForm && (
                                        <button type="button" onClick={handleAddMember} className="btn-add-new" style={{ padding: '5px 10px', fontSize: '12px', margin: 0 }}>
                                            <i className="fa-solid fa-plus"></i> Thêm thành viên
                                        </button>
                                    )}
                                </div>

                                <table className="custom-table" style={{ width: '100%', fontSize: '13px' }}>
                                    <thead>
                                        <tr>
                                            <th width="10%">STT</th>
                                            <th width="60%">Thành viên</th>
                                            <th width="20%" style={{ textAlign: 'center' }}>Là Chủ bằng (Tác giả chính)</th>
                                            {!isReadOnlyForm && <th width="10%" style={{ textAlign: 'center' }}>Xóa</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {members.length === 0 ? (
                                            <tr><td colSpan={isReadOnlyForm ? "3" : "4"} style={{ textAlign: 'center', padding: '15px', color: '#999' }}>Vui lòng thêm thành viên</td></tr>
                                        ) : members.map((m, i) => (
                                            <tr key={i}>
                                                <td style={{ textAlign: 'center' }}>{i + 1}</td>
                                                <td>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                        <SearchableFocusDropdown
                                                            value={m.userId}
                                                            options={userOptions}
                                                            onChange={(e) => handleMemberChange(i, 'userId', e.value)}
                                                            placeholder="Chọn thành viên"
                                                            disabled={isReadOnlyForm}
                                                            itemTemplate={userItemTemplate}
                                                            panelUniqueClass={`custom-dropdown-panel-ip-${i}`}
                                                        />
                                                        <input
                                                            type="text"
                                                            className="form-input"
                                                            placeholder="Hoặc nhập họ tên nếu ở ngoài trường"
                                                            value={m.fullName || ''}
                                                            onChange={(e) => handleMemberChange(i, 'fullName', e.target.value)}
                                                            disabled={isReadOnlyForm}
                                                            style={{ backgroundColor: m.userId ? '#f1f5f9' : '#fff', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                                        />
                                                    </div>
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={m.isFirstAuthor}
                                                        onChange={(e) => handleMemberChange(i, 'isFirstAuthor', e.target.checked)}
                                                        disabled={isReadOnlyForm}
                                                        style={{ cursor: isReadOnlyForm ? 'not-allowed' : 'pointer', width: '16px', height: '16px' }}
                                                    />
                                                </td>
                                                {!isReadOnlyForm && (
                                                    <td style={{ textAlign: 'center' }}>
                                                        <i className="fa-solid fa-xmark" style={{ color: '#e74c3c', cursor: 'pointer', fontSize: '16px' }} onClick={() => handleRemoveMember(i)}></i>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <p style={{ fontSize: '12px', color: '#e67e22', fontStyle: 'italic', marginTop: '10px', marginBottom: 0 }}>
                                    * Luật tính điểm Bằng Sáng chế/SHTT: Chủ bằng nhận 1/3 điểm. Phần 2/3 chia đều cho tất cả.
                                </p>
                            </div>
                        )}

                        {is2GVStudentGuide && (
                            <div style={{ marginTop: '20px', marginBottom: '20px', padding: '15px', border: '1px solid #3498db', borderRadius: '5px', backgroundColor: '#f0f8ff' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                    <label style={{ fontWeight: 'bold', color: '#0056b3' }}>Kê khai Giảng viên hướng dẫn *</label>
                                </div>
                                <table className="custom-table" style={{ width: '100%', fontSize: '13px' }}>
                                    <thead>
                                        <tr>
                                            <th width="10%">STT</th>
                                            <th width="90%">Thành viên</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {members.slice(0, 2).map((m, i) => (
                                            <tr key={i}>
                                                <td style={{ textAlign: 'center', fontWeight: 'bold' }}>Giảng viên {i + 1}</td>
                                                <td>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                        <SearchableFocusDropdown
                                                            value={m.userId}
                                                            options={userOptions}
                                                            onChange={(e) => handleMemberChange(i, 'userId', e.value)}
                                                            placeholder="Chọn Giảng viên có trong hệ thống"
                                                            disabled={isReadOnlyForm}
                                                            itemTemplate={userItemTemplate}
                                                            panelUniqueClass={`custom-dropdown-panel-2gv-${i}`}
                                                        />
                                                        <input
                                                            type="text"
                                                            className="form-input"
                                                            placeholder="Hoặc nhập họ tên nếu ở ngoài trường"
                                                            value={m.fullName || ''}
                                                            onChange={(e) => handleMemberChange(i, 'fullName', e.target.value)}
                                                            disabled={isReadOnlyForm}
                                                            style={{ backgroundColor: m.userId ? '#f1f5f9' : '#fff', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        <div style={{ fontSize: '13px', color: '#666', fontStyle: 'italic', marginBottom: '20px', lineHeight: '1.5' }}>
                            Lưu ý: Số lượng thành viên tham gia tính cho mỗi nội dung và điểm công trình sẽ chia đều cho mỗi người.
                            Trường hợp có nhiều nhóm thì thực hiện kê khai nhiều lần tương ứng với số lượng thành viên mỗi nhóm.
                        </div>

                        <div className="form-group" style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Ghi chú *</label>
                            <textarea name="Notes" className="form-input" rows="3" value={formData.Notes} onChange={handleChange} placeholder="Nhập ghi chú" required disabled={isReadOnlyForm} style={{ resize: 'vertical' }}></textarea>
                        </div>

                        <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Tệp tin minh chứng *</label>

                            <p style={{ color: '#e74c3c', fontStyle: 'italic', marginBottom: '15px', marginTop: '-5px', fontSize: '14px' }}>
                                * Chú ý: Hệ thống chỉ nhận tệp minh chứng ở định dạng PDF, JPG/JPEG và PNG. Dung lượng tối đa 5MB / tệp
                            </p>

                            {(existingFiles.length > 0 || pendingFiles.length > 0) && (
                                <div style={{ overflowX: 'auto', marginBottom: '15px' }}>
                                    <table className="custom-table" style={{ width: '100%', minWidth: '400px', border: '1px solid #eee', marginBottom: 0 }}>
                                        <thead style={{ backgroundColor: '#f9f9f9' }}>
                                            <tr>
                                                <th width="50px" style={{ textAlign: 'center' }}>TT</th>
                                                <th>Mô tả</th>
                                                <th width="35%">Minh chứng</th>
                                                {!isReadOnlyForm && <th width="80px" style={{ textAlign: 'center' }}>Chức năng</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {existingFiles.map((file, idx) => (
                                                <tr key={`ext-${idx}`}>
                                                    <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                                                    <td>
                                                        {isReadOnlyForm ? (
                                                            <span>{file.description || ''}</span>
                                                        ) : (
                                                            <input
                                                                type="text"
                                                                className="form-input"
                                                                placeholder="Nhập thông tin mô tả"
                                                                value={file.description || ''}
                                                                onChange={(e) => handleFileDescChange(idx, e.target.value)}
                                                                style={{ width: '100%', padding: '8px', border: 'none', background: 'transparent', borderBottom: '1px solid #eee', borderRadius: 0 }}
                                                            />
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span onClick={() => downloadIndividualFile(file)} style={{ color: '#3498db', cursor: 'pointer', wordBreak: 'break-all' }} title="Nhấn để tải về">
                                                            {file.name}
                                                        </span>
                                                    </td>
                                                    {!isReadOnlyForm && (
                                                        <td style={{ textAlign: 'center' }}>
                                                            <button type="button" onClick={() => removeExistingFile(file.fileId || file.url || file.link || file.data)} style={{ background: '#fff', border: '1px solid #e74c3c', color: '#e74c3c', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer' }}>
                                                                <i className="fa-solid fa-trash-can"></i>
                                                            </button>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                            {pendingFiles.map((file, idx) => (
                                                <tr key={`pend-${idx}`}>
                                                    <td style={{ textAlign: 'center' }}>{existingFiles.length + idx + 1}</td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            className="form-input"
                                                            placeholder="Nhập thông tin mô tả"
                                                            value={file.description || ''}
                                                            onChange={(e) => {
                                                                const newFiles = [...pendingFiles];
                                                                newFiles[idx].description = e.target.value;
                                                                setPendingFiles(newFiles);
                                                            }}
                                                            style={{ width: '100%', padding: '8px', border: 'none', background: 'transparent', borderBottom: '1px solid #eee', borderRadius: 0 }}
                                                            disabled={isReadOnlyForm}
                                                        />
                                                    </td>
                                                    <td>
                                                        <span style={{ color: '#2ecc71', wordBreak: 'break-all' }}>
                                                            {file.name} <i className="fa-solid fa-cloud-arrow-up"></i> (Mới)
                                                        </span>
                                                    </td>
                                                    {!isReadOnlyForm && (
                                                        <td style={{ textAlign: 'center' }}>
                                                            <button type="button" onClick={() => removePendingFile(idx)} style={{ background: '#fff', border: '1px solid #e74c3c', color: '#e74c3c', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer' }}>
                                                                <i className="fa-solid fa-trash-can"></i>
                                                            </button>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {!isReadOnlyForm && (
                                <div style={{ border: '2px dashed #3498db', borderRadius: '8px', padding: '30px', textAlign: 'center', backgroundColor: '#ebf5fb', position: 'relative', overflow: 'hidden' }}>
                                    <input
                                        type="file"
                                        multiple
                                        accept=".pdf, .jpg, .jpeg, .png, application/pdf, image/jpeg, image/png"
                                        onChange={handleMultiFileChange}
                                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', zIndex: 10 }}
                                        title="Nhấn để chọn tệp"
                                    />
                                    {(existingFiles.length > 0 || pendingFiles.length > 0) ? (
                                        <>
                                            <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: '35px', color: '#3498db', marginBottom: '10px' }}></i>
                                            <p style={{ margin: '0 0 10px 0', color: '#003399', fontWeight: 'bold', fontSize: '15px' }}>Đã chọn {existingFiles.length + pendingFiles.length} tệp minh chứng</p>
                                            <button type="button" style={{ padding: '6px 15px', backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px', color: '#333' }}>Thêm tệp tin khác</button>
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: '40px', color: '#3498db', marginBottom: '10px' }}></i>
                                            <p style={{ margin: '0 0 10px 0', color: '#003399', fontWeight: 'bold', fontSize: '15px' }}>Chưa có tệp nào được chọn</p>
                                            <button type="button" style={{ padding: '6px 15px', backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px', color: '#333' }}>Chọn tệp từ thiết bị</button>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                    </form>
                </div>

                <div className="modal-footer" style={{ borderTop: '1px solid #eee', padding: '15px 25px' }}>
                    {isReadOnlyForm ? (
                        <button type="button" className="btn-submit" onClick={onClose}>Đóng</button>
                    ) : (
                        <>
                            <button type="button" className="btn-cancel" onClick={onClose} disabled={isSubmitting}>Hủy</button>
                            <button type="submit" form="publicationOtherForm" className="btn-submit" disabled={isSubmitting}>
                                {isSubmitting ? <><i className="fa-solid fa-spinner fa-spin" style={{ marginRight: '5px' }}></i> Đang lưu...</> : 'Hoàn tất'}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PublicationOtherForm;